// External store links (e.g. the US store) in the localization dropdown.
// Computes the cross-domain URL at click time so it survives section re-renders
// (theme editor, hot reload): adds the `?country=XX` deep link and the `?cs=1`
// fallback marker, and forwards the current path only when that path is
// guaranteed to resolve on the other store.

// Product, collection and page handles are not shared between stores, so
// forwarding an arbitrary path lands the customer on a 404. Only these resolve
// on every Shopify storefront.
const PORTABLE_PATHS = new Set(['', 'search', 'collections/all', 'cart']);

// These params cross an origin boundary, so this is an allowlist rather than a
// copy of everything the current URL happens to carry (`preview_theme_id`,
// discount codes, session hints, whatever an app appended).
const FORWARDED_PARAM_PREFIXES = ['utm_'];
const FORWARDED_PARAMS = new Set(['gclid', 'fbclid', 'ttclid', 'q']);

/**
 * @param {string} key - The query parameter name.
 * @returns {boolean} Whether the parameter may be carried to the other store.
 */
function isForwardedParam(key) {
  return FORWARDED_PARAMS.has(key) || FORWARDED_PARAM_PREFIXES.some((prefix) => key.startsWith(prefix));
}

/**
 * The current path relative to the locale root, without surrounding slashes.
 *
 * @returns {string} e.g. `collections/all` or `''` on the homepage.
 */
function currentRelativePath() {
  const root = window.Shopify?.routes?.root ?? '/';
  let path = window.location.pathname;

  if (path.startsWith(root)) path = path.slice(root.length);

  return path.replace(/^\/+/, '').replace(/\/+$/, '');
}

document.addEventListener('click', (event) => {
  if (event.defaultPrevented || event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

  const link = event.target instanceof Element ? event.target.closest('a[data-cs-link]') : null;
  if (!(link instanceof HTMLAnchorElement)) return;

  let url;
  try {
    url = new URL(link.dataset.csBase || link.href);
  } catch {
    return;
  }

  const path = currentRelativePath();
  const keepPath = PORTABLE_PATHS.has(path);

  if (keepPath && path) {
    url.pathname = `${url.pathname.replace(/\/+$/, '')}/${path}`;
  }

  const params = new URLSearchParams(url.search);

  for (const [key, value] of new URLSearchParams(window.location.search)) {
    if (isForwardedParam(key)) params.set(key, value);
  }

  const country = link.dataset.csCountry;
  if (country) params.set('country', country);
  params.set('cs', '1');

  url.search = params.toString();
  // An anchor only means anything on the page it came from.
  url.hash = keepPath ? window.location.hash : '';

  event.preventDefault();
  window.location.assign(url.toString());
});
